import React from 'react'
import AttachmentIcon from '@material-ui/icons/Attachment'

export interface KeylessFileFallbackProps {
  file: { src?: string; title?: string }
  className?: string
  onClick?: React.MouseEventHandler
}

// `src` comes from the public API caller, so only link out to a web URL; a
// `javascript:` or `data:` src renders the plain icon instead.
export function isWebUrl(src?: string): boolean {
  return /^https?:\/\//i.test(src ?? '')
}

/**
 * Stands in for a video or PDF whose `src` was never copied into S3 or Cloudinary
 * (see isKeylessRemoteFile). There is no key to build a player or page image from,
 * so show the attachment icon. With no click handler it links to the source URL;
 * with one (editable grids) the click keeps opening the file viewer (#24892).
 */
export default function KeylessFileFallback({ file, className, onClick }: KeylessFileFallbackProps) {
  const icon = <AttachmentIcon color="inherit" fontSize={'inherit'} />
  const label = file.title || file.src

  if (onClick || !isWebUrl(file.src)) {
    return (
      <div className={className} onClick={onClick} title={label} aria-label={label} data-testid="keyless-file-fallback">
        {icon}
      </div>
    )
  }

  return (
    <a
      className={className}
      href={file.src}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      style={{ color: 'inherit' }}
      data-testid="keyless-file-fallback"
    >
      {icon}
    </a>
  )
}
